import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Drawer, Table, Switch, Input, Button, message, Tag, Card, Tooltip } from 'antd';
import { Skeleton } from 'antd';
import { request } from '~/_shared/instance';
import { frequency } from '~/_domains/tasks/helpers';
import { FaTasks, FaCheckCircle, FaCalendarAlt, FaCalendarDay, FaCalendarWeek, FaCalendar } from 'react-icons/fa';
import styled from 'styled-components';

const FrequencyIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Circle = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color};
`;

const Count = styled.span`
  font-size: 10px;
  color: #666;
`;

const FrequencyContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 4px;
`;

const getDepartmentInfo = (departmentId) => {
  const departments = {
    1: { name: 'Societário', color: 'blue' },
    2: { name: 'Administrativo', color: 'green' },
    3: { name: 'Contábil', color: 'purple' },
    4: { name: 'Fiscal', color: 'orange' },
    5: { name: 'Pessoal', color: 'cyan' },
    6: { name: 'Sucesso do Cliente', color: 'magenta' }
  };
  return departments[departmentId] || { name: 'Desconhecido', color: 'default' };
};

const CustomerTasks = ({ visible, customer, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedTasks, setSelectedTasks] = useState([]);

  const loadData = useCallback(async () => {
    if (visible && customer) {
      setInitialLoading(true);
      try {
        const response = await request('GET', `/customer/${customer.id}/tasks`, null, false);
        const tasksData = response.data.data || [];
        setTasks(tasksData);
        setSelectedTasks(tasksData.filter(task => task.is_assigned).map(task => task.id));
      } catch (error) {
        // Error is already handled by the request interceptor
      } finally {
        setInitialLoading(false);
      }
    }
  }, [visible, customer]);

  useEffect(() => {
    let isSubscribed = true;

    const fetchData = async () => {
      if (visible && customer) {
        setInitialLoading(true);
        try {
          const response = await request('GET', `/customer/${customer.id}/tasks`, null, false);
          if (isSubscribed) {
            const tasksData = response.data.data || [];
            setTasks(tasksData);
            setSelectedTasks(tasksData.filter(task => task.is_assigned).map(task => task.id));
          }
        } catch (error) {
          // Error is already handled by the request interceptor
        } finally {
          if (isSubscribed) {
            setInitialLoading(false);
          }
        }
      }
    };

    fetchData();

    return () => {
      isSubscribed = false;
    };
  }, [visible, customer]);

  const filteredTasks = useMemo(() => {
    return tasks
      .filter(task => {
        const matchesSearch = searchQuery 
          ? task.name.toLowerCase().includes(searchQuery.toLowerCase())
          : true;
        return matchesSearch;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tasks, searchQuery]);

  const handleTaskToggle = useCallback(async (taskId, checked) => {
    if (!customer) return;
    
    try {
      setLoading(true);
      
      if (checked) {
        // Add task
        const response = await request('POST', `/customer/${customer.id}/tasks`, 
          { task_ids: [taskId] }, false);
        
        if (response.data.status === 'success') {
          const { added_tasks, already_assigned } = response.data.data;
          if (already_assigned && already_assigned.length > 0) {
            message.info('Esta tarefa já está atribuída');
            return;
          }
          setSelectedTasks(prev => [...prev, taskId]);
          message.success('Tarefa atribuída com sucesso!');
        }
      } else {
        // Remove task
        await request('DELETE', `/customer/${customer.id}/tasks`, 
          { task_ids: [taskId] }, false);
        setSelectedTasks(prev => prev.filter(id => id !== taskId));
        message.success('Tarefa removida com sucesso!');
      }
    } catch (error) {
      // Error is already handled by the request interceptor
    } finally {
      setLoading(false);
    }
  }, [customer]);

  const handleSave = useCallback(() => {
    message.success('Tarefas atualizadas com sucesso!');
    onClose();
  }, [onClose]);

  const taskColumns = useMemo(() => [
    {
      title: "",
      key: "toggle",
      render: (text, record) => (
        <Switch
          onChange={checked => handleTaskToggle(record.id, checked)}
          checked={selectedTasks.includes(record.id)}
        />
      )
    },
    { 
      title: "Nome", 
      dataIndex: "name", 
      key: "name",
      filteredValue: searchQuery ? [searchQuery] : null,
      onFilter: (value, record) => record.name.toLowerCase().includes(value.toLowerCase())
    },
    { 
      title: "Departamento",
      key: "department",
      render: (_, record) => {
        const { name, color } = getDepartmentInfo(parseInt(record.department));
        return <Tag color={color}>{name}</Tag>;
      },
      filters: [
        { text: 'Societário', value: '1' },
        { text: 'Administrativo', value: '2' },
        { text: 'Contábil', value: '3' },
        { text: 'Fiscal', value: '4' },
        { text: 'Pessoal', value: '5' },
        { text: 'Sucesso do Cliente', value: '6' }
      ],
      onFilter: (value, record) => record.department === value
    },
    { 
      title: "Quando?", 
      key: "frequency", 
      render: (_, record) => frequency(record.frequency, record)
    },
  ], [searchQuery, selectedTasks, handleTaskToggle]);

  const renderSkeleton = useCallback(() => (
    <div style={{ padding: '16px' }}>
      <div style={{ marginBottom: 16 }}>
        <Skeleton active title={false} paragraph={{ rows: 1, width: 200 }} />
      </div>
      <div style={{ marginBottom: 24 }}>
        <Skeleton active title={false} paragraph={{ rows: 1, width: 200 }} />
      </div>
      <Skeleton active title={false} paragraph={{ rows: 10 }} />
    </div>
  ), []);

  const getDrawerTitle = useCallback(() => {
    const customerName = customer && customer.name ? customer.name : '';
    return `Tarefas - ${customerName}`;
  }, [customer]);

  return (
    <Drawer
      title={getDrawerTitle()}
      placement="right"
      onClose={onClose}
      visible={visible}
      width={800}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancelar
          </Button>
          <Button onClick={handleSave} type="primary" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      }
    >
      {initialLoading ? (
        renderSkeleton()
      ) : (
        <>
          <div style={{ marginBottom: 16, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Card 
              style={{ 
                flex: 1, 
                minWidth: '160px',
                background: '#f6ffed',
                border: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                padding: '8px'
              }}
              bodyStyle={{ padding: '8px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaCheckCircle size={20} color="#52c41a" />
                <div>
                  <div style={{ color: '#666', fontSize: '12px' }}>Tarefas atribuídas</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                    {selectedTasks.length}
                  </div>
                  <FrequencyContainer>
                    <Tooltip title="Tarefas mensais atribuídas">
                      <FrequencyIndicator>
                        <Count>
                          {tasks.filter(task => task.frequency === 1 && selectedTasks.includes(task.id)).length}
                        </Count>
                        <Circle color="#722ed1" />
                      </FrequencyIndicator>
                    </Tooltip>
                    <Tooltip title="Tarefas trimestrais atribuídas">
                      <FrequencyIndicator>
                        <Count>
                          {tasks.filter(task => task.frequency === 2 && selectedTasks.includes(task.id)).length}
                        </Count>
                        <Circle color="#fa8c16" />
                      </FrequencyIndicator>
                    </Tooltip>
                    <Tooltip title="Tarefas semestrais atribuídas">
                      <FrequencyIndicator>
                        <Count>
                          {tasks.filter(task => task.frequency === 3 && selectedTasks.includes(task.id)).length}
                        </Count>
                        <Circle color="#f5222d" />
                      </FrequencyIndicator>
                    </Tooltip>
                    <Tooltip title="Tarefas anuais atribuídas">
                      <FrequencyIndicator>
                        <Count>
                          {tasks.filter(task => task.frequency === 4 && selectedTasks.includes(task.id)).length}
                        </Count>
                        <Circle color="#13c2c2" />
                      </FrequencyIndicator>
                    </Tooltip>
                  </FrequencyContainer>
                </div>
              </div>
            </Card>
            <Card 
              style={{ 
                flex: 1, 
                minWidth: '160px',
                background: '#f0f5ff',
                border: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                padding: '8px'
              }}
              bodyStyle={{ padding: '8px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaTasks size={20} color="#1890ff" />
                <div>
                  <div style={{ color: '#666', fontSize: '12px' }}>Total de tarefas</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
                    {tasks.length}
                  </div>
                </div>
              </div>
            </Card>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Input 
              placeholder="Buscar tarefas" 
              value={searchQuery} 
              onChange={e => setSearchQuery(e.target.value)} 
              style={{ width: 200 }}
            />
          </div>
          <Table
            rowKey="id"
            columns={taskColumns}
            dataSource={filteredTasks}
            pagination={false}
            loading={loading}
          />
        </>
      )}
    </Drawer>
  );
};

export default CustomerTasks; 